import React, { useRef, useState, useEffect } from "react";

import gStaticMapSignature from 'shared-utils/src/gStaticMapSignature';
import { GMAPS_API_KEY, GMAPS_SECRET_SIGNATURE } from 'shared-constants/src/gmaps';

import getNearbyShape from '@helpers/getNearbyShape';

import * as staticMapShapes from '@helpers/staticMapShapes';

const ITALY_BBOX = [[48.809821242849445,0.8928202195415906],[34.45592178596872,23.15112100079159]];

export default function StaticMapWithDrawings({
  filters = {},
  containerClassName = null,
  mapImgClassName = null,
  loaderComponent = null,
  mapImgAlt = 'Area su mappa',
}) {
  const mapelRef = useRef(null);

  const [mapData, setMapData] = useState(null);
  const [isImgLoaded, setIsImgLoaded] = useState(false);

  const getMapData = async ({ sizes }) => {
    const createMapData = async () => {
      let drawData;
      if (filters.geobounds) {
        drawData = `|${staticMapShapes.getBBoxPath(filters.geobounds.bbox)}`;
      } else if (filters.geocircle) {
        drawData = `${staticMapShapes.getCirclePath(filters.geocircle.circle[0].center[0], filters.geocircle.circle[0].center[1], Math.floor(filters.geocircle.circle[0].distance))}`;
      } else if (filters.geopolygon) {
        drawData = `|${staticMapShapes.getPolyPath(filters.geopolygon.polygon)}`; 
      } else if (filters.nearby) {
        const nearbyShape = await getNearbyShape({
          coords: filters.nearby.location,
          travelMode: filters.nearby.travel_mode,
          travelTime: filters.nearby.travel_time,
        });
        drawData = `|${staticMapShapes.getPolyPath(nearbyShape)}`;
      } else {
        drawData = `|${staticMapShapes.getBBoxPath(ITALY_BBOX)}`;
      }

      return `https://maps.googleapis.com${gStaticMapSignature(`/maps/api/staticmap?path=color:0x505a65ff|weight:2|fillcolor:0x69768455${drawData}&size=${sizes.w}x${sizes.h}&key=${GMAPS_API_KEY}&style=feature:transit.station|visibility:off&scale=2`, GMAPS_SECRET_SIGNATURE)}`;       
    };

    setMapData({
      url: await createMapData(),
      sizes,
    });
  };

  // Stable dep key — re-runs effect only when the actual geo shape data changes.
  const geoKey = JSON.stringify(
    filters.geobounds || filters.geocircle || filters.geopolygon || filters.nearby || null
  );

  // ResizeObserver re-fetches the static map when the container resizes
  // (e.g. modal shrinks from 1180→620 after map panel confirm).
  // geoKey dep ensures a fresh observer (with updated filters closure) on shape change.
  useEffect(() => {
    if (!mapelRef.current) return;
    let lastW = 0, lastH = 0;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      const w = Math.round(width);
      const h = Math.round(height);
      if (w > 0 && h > 0 && (w !== lastW || h !== lastH)) {
        lastW = w;
        lastH = h;
        setIsImgLoaded(false);
        getMapData({ sizes: { w, h } });
      }
    });
    observer.observe(mapelRef.current);
    return () => observer.disconnect();
  }, [geoKey]);

  return (
    <div ref={mapelRef} className={containerClassName}>
      {mapData
        ? (
      <img
        src={mapData.url}
        alt={mapImgAlt}
        width={mapData.sizes.w}
        height={mapData.sizes.h}
        className={`${mapImgClassName}${isImgLoaded ? ' is-loaded' : ''}`}
        onLoad={() => setIsImgLoaded(true)}
      />) : loaderComponent}
    </div>
  );
}